import { supabase } from '@/lib/supabaseClient';

/**
 * SUB-3.14 · FR60. What an account may be told about outside management access
 * over ITSELF.
 *
 * ⚠️ THE BOUNDARY, BECAUSE IT IS EASY TO WIDEN BY ACCIDENT. An account may know
 * WHO HAS ACCESS TO IT. It may not learn that a hierarchy exists, that it has
 * siblings, or anything about any other account (ISO1 / SUB-2.12 / FR19). The
 * server function `account_management_access()` takes no parameter and returns
 * exactly these fields; nothing here should ever ask it for more, and no caller
 * should join this to a tenant list.
 */
export interface ManagementAccessHolder {
  userId: string;
  name: string | null;
  email: string | null;
  /** When THIS person's access to this account was granted, not when the relationship began. */
  grantedAt: string | null;
}

export interface ManagementAccess {
  /** Null when the organization's name could not be resolved. Access is still real. */
  parentCompanyName: string | null;
  /** The earliest grant on record for this account. */
  grantedAt: string | null;
  holders: ManagementAccessHolder[];
}

const asString = (v: unknown): string | null =>
  typeof v === 'string' && v.trim() !== '' ? v : null;

/**
 * Turn the rpc payload into something renderable, or into NULL.
 *
 * There is no third outcome on purpose. A partially understood payload rendered
 * as a panel headed "an organization holds management access" with no names under
 * it is worse than no panel: it tells an account owner something alarming and
 * gives them nothing to act on.
 */
export function parseManagementAccess(raw: unknown): ManagementAccess | null {
  const payload = Array.isArray(raw) ? raw[0] : raw;
  if (!payload || typeof payload !== 'object') return null;

  const record = payload as Record<string, unknown>;
  if (!Array.isArray(record.holders)) return null;

  const holders: ManagementAccessHolder[] = record.holders.flatMap((entry) => {
    if (!entry || typeof entry !== 'object') return [];
    const h = entry as Record<string, unknown>;
    const userId = asString(h.user_id);
    if (!userId) return [];
    return [
      {
        userId,
        name: asString(h.name),
        email: asString(h.email),
        grantedAt: asString(h.granted_at),
      },
    ];
  });

  if (holders.length === 0) return null;

  return {
    parentCompanyName: asString(record.parent_company_name),
    grantedAt: asString(record.granted_at),
    holders,
  };
}

export function holderLabel(holder: ManagementAccessHolder): string {
  return holder.name ?? holder.email ?? 'Unnamed user';
}

/**
 * Ask the server who has management access to the caller's current account.
 *
 * ⚠️ A FAILURE IS NOT "NOBODY HAS ACCESS", AND THE LOG IS NOT OPTIONAL. This is
 * the only surface that answers FR60, so a revoked grant, a renamed function or a
 * proxy rejecting the call must not silently produce a clean-looking account.
 * The caller still renders nothing, because a scary error box on every ordinary
 * account is not the alternative, but the reason is in the console.
 */
export async function fetchManagementAccess(): Promise<ManagementAccess | null> {
  try {
    const { data, error } = await supabase.rpc('account_management_access');

    if (error) {
      // PGRST202 here means 20260813j has not been applied in this environment.
      console.error(
        '[managementAccess] Could not read management access for this account. ' +
          'The Settings notice will not render, which is NOT the same as nobody having access:',
        error
      );
      return null;
    }

    return parseManagementAccess(data);
  } catch (e) {
    console.error('[managementAccess] Unexpected failure reading management access:', e);
    return null;
  }
}
